/**
 * BackLink - "go back to where I came from"
 * -----------------------------------------
 * Uses actual browser history rather than a fixed destination, because you
 * can reach the same page from several places. A student profile might be
 * opened from the marketplace, the student directory, or the list of people
 * who applied for your chore - and "Back to the marketplace" is wrong in two
 * of those three.
 *
 * If there is nowhere to go back to - somebody opened the link directly, or
 * followed it from an email - it falls back to the page given in `to`, so the
 * button is never a dead end.
 *
 *   <BackLink to="/marketplace" label="Back to the marketplace" />
 */

import { useNavigate, useLocation, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import { useI18n } from '../hooks/useI18n';

export default function BackLink({ to = '/', label, style }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const text = label || t('common.back');
  const location = useLocation();

  // React Router keeps an index of where we are in its history stack.
  // Anything above 0 means there is a previous page within the app.
  const canGoBack = (location.key && location.key !== 'default') || window.history.state?.idx > 0;

  const baseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    marginBottom: 'var(--space-sm)',
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    fontSize: 'var(--text-sm)',
    color: 'var(--color-blue)',
    cursor: 'pointer',
    ...style,
  };

  if (!canGoBack) {
    return (
      <Link to={to} style={baseStyle}>
        <ArrowLeft size={14} aria-hidden="true" />
        {text}
      </Link>
    );
  }

  return (
    <button type="button" onClick={() => navigate(-1)} style={baseStyle}>
      <ArrowLeft size={14} aria-hidden="true" />
      {text}
    </button>
  );
}
